import { IconKey } from '/imports/ui/Icon';
import { strings } from '/imports/strings';

/**
 * The console's navigable destinations, in sidebar order.
 *
 * Extracted from Nav so the command palette can offer exactly what the sidebar
 * offers. Duplicating the list would guarantee drift: a page added to one and
 * not the other is invisible in the palette or dead in the nav, and neither
 * failure is loud.
 *
 * Section labels are entries rather than nesting so a single ordered array
 * drives both the sidebar's group headings and the palette's result groups.
 */
export interface NavDestination {
  id: string;
  path: string;
  text: string;
  icon?: IconKey;
  /** Extra words the palette matches on but the sidebar never shows. */
  keywords?: string[];
}

export type NavEntry = { sectionLabel: string } | NavDestination;

export const isSection = (e: NavEntry): e is { sectionLabel: string } => 'sectionLabel' in e;

export interface NavAudience {
  isSuperUser: boolean;
  isAdminOrSuper: boolean;
}

export const buildNavEntries = ({ isSuperUser, isAdminOrSuper }: NavAudience): NavEntry[] => {
  const entries: NavEntry[] = [];
  // Section order: Bot Admin -> Bot Reference -> Bot Logs -> Messages -> Help.
  if (isSuperUser) {
    entries.push({ sectionLabel: 'Bot Admin' });
    entries.push({ id: 'dashboard', path: '/dashboard', text: 'Dashboard', icon: 'queryStats', keywords: ['analytics', 'stats', 'overview', 'kpi'] });
    entries.push({ id: 'performance', path: '/performance', text: 'Performance', icon: 'trendingUp', keywords: ['margin', 'profit', 'pnl', 'funnel', 'completion'] });
    entries.push({ id: 'trade-inspector', path: '/trade-inspector', text: 'Trade Inspector', icon: 'searchDoc', keywords: ['sessions', 'funnel', 'forensic', 'why', 'cancelled'] });
    entries.push({ id: 'traders', path: '/traders', text: 'Traders', icon: 'group', keywords: ['customers', 'retention', 'cohort', 'leaderboard', 'repeat'] });
    entries.push({ id: 'fleet-pnl', path: '/fleet-pnl', text: 'Fleet P&L', icon: 'wallet', keywords: ['per bot', 'dead stock', 'idle', 'profit'] });
    entries.push({ id: 'pricing', path: '/pricing', text: 'Pricing', icon: 'tag', keywords: ['competitiveness', 'goatbots', 'margin', 'spread'] });
    entries.push({ id: 'bot-health', path: '/bot-health', text: 'Bot Health', icon: 'pulse', keywords: ['uptime', 'crashes', 'stalls', 'fleet', 'incidents'] });
    entries.push({ id: 'ad-attribution', path: '/ad-attribution', text: 'Ad Attribution', icon: 'target' });
    entries.push({ id: 'bots', path: '/bots', text: 'Bots', icon: 'robot', keywords: ['fleet', 'runner', 'status'] });
    entries.push({ id: 'fleets', path: '/fleets', text: 'Fleets', icon: 'boxes' });
    entries.push({ id: 'bot-messages', path: '/bot-messages', text: 'Chat Messages', icon: 'chat' });
    entries.push({ id: 'card-lists', path: '/card-lists', text: 'Card Lists', icon: 'list' });
    entries.push({ id: 'inventory-policies', path: '/inventory-policies', text: 'Inventory Policies', icon: 'packageBox', keywords: ['stock', 'quantities', 'qty', 'deplete'] });
    entries.push({ id: 'cards', path: '/cards', text: 'Cards', icon: 'layers', keywords: ['inventory', 'price'] });
    entries.push({ id: 'buy-targets', path: '/buy-targets', text: 'Buy Targets', icon: 'target' });
    entries.push({ id: 'sell-targets', path: '/sell-targets', text: 'Sell Targets', icon: 'coins' });
    entries.push({ id: 'assets', path: '/assets', text: 'Assets', icon: 'packageBox', keywords: ['stock', 'collection'] });
    entries.push({ id: 'musers', path: '/musers', text: 'MTGO Users', icon: 'person', keywords: ['customer', 'partner'] });
    entries.push({ id: 'escrows', path: '/escrows', text: 'Escrows', icon: 'handshake', keywords: ['trade'] });
    entries.push({ id: 'trade-items', path: '/trade-items', text: 'Trade Items', icon: 'receipt', keywords: ['sales', 'invoice'] });
    entries.push({ id: 'analytics-notes', path: '/analytics-notes', text: 'Analytics Notes', icon: 'bookOpen', keywords: ['glossary', 'definitions', 'lifo', 'caveats', 'how to read'] });
    entries.push({ sectionLabel: 'Bot Reference' });
    entries.push({ id: 'rarities', path: '/rarities', text: 'Rarities', icon: 'gem' });
    entries.push({ id: 'card-names', path: '/card-names', text: 'Card Names', icon: 'list' });
    entries.push({ id: 'sets', path: '/sets', text: 'Sets', icon: 'boxes' });
    entries.push({ id: 'card-sources', path: '/card-sources', text: 'Card Sources', icon: 'database' });
    entries.push({ id: 'price-sources', path: '/price-sources', text: 'Price Sources', icon: 'plug' });
    entries.push({ id: 'blacklists', path: '/blacklists', text: 'Blacklists', icon: 'block' });
    entries.push({ id: 'buy-counters', path: '/buy-counters', text: 'Buy Counters', icon: 'hash' });
    entries.push({ sectionLabel: 'Bot Logs' });
    entries.push({ id: 'log-activities', path: '/log-activities', text: 'Log Activities', icon: 'fileText' });
    entries.push({ id: 'log-packets', path: '/log-packets', text: 'Log Packets', icon: 'terminal' });
  }
  entries.push({ sectionLabel: strings.Messages() });
  entries.push({ id: 'messages', path: '/messages', text: strings.Messages(), icon: 'chat' });
  entries.push({ sectionLabel: strings.Help() });
  entries.push({ id: 'support', path: '/support', text: strings.Support(), icon: 'help' });
  entries.push({ id: 'manage', path: '/manage', text: strings.Manage(), icon: 'manageAccounts' });
  if (isAdminOrSuper) {
    entries.push({ id: 'devtools', path: '/devtools', text: strings.DevTools(), icon: 'wrench' });
  }
  return entries;
};

/** Flatten to destinations, tagging each with the section it appeared under. */
export const navDestinations = (entries: NavEntry[]): (NavDestination & { section: string })[] => {
  const out: (NavDestination & { section: string })[] = [];
  let section = '';
  for (const entry of entries) {
    if (isSection(entry)) section = entry.sectionLabel;
    else out.push({ ...entry, section });
  }
  return out;
};
